// /////////////////////////////////////////////////////////////
// [ファイル] WorkspaceAssistantPanel.tsx
// [概要] Genkit 駆動の Workspace アシスタントの会話 UI。ユーザーの自然言語指示を
//        送信し、AI がカレンダー等をツール操作した結果を会話形式で表示する。
//        左サイドバーで会話セッション (Firestore 永続化) を切り替え、
//        入力欄下に Workflow チップ、ヘッダーから Agent 定義の閲覧ができる。
//        操作完了後は onMutated でカレンダー表示の再取得を促す。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Collapse,
  Select,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LoadingView from './LoadingView';
import MarkdownMessage from './MarkdownMessage';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_DANGER_BUTTON_COMPACT_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import {
  useWorkspaceAssistant,
  type AttachedFileInput,
} from '../hooks/useWorkspaceAssistant';
import { useNotification } from '../providers/NotificationProvider';
import { useAssistantThinking } from '../providers/AssistantThinkingProvider';
import {
  useAssistantSessions,
  type AssistantSession,
  type AssistantMessage,
  type AgentType,
} from '../hooks/useAssistantSessions';
import {
  useAssistantFeedback,
  FEEDBACK_CATEGORIES,
  type FeedbackRating,
} from '../hooks/useAssistantFeedback';
import { useAssistantAgents } from '../hooks/useAssistantAgents';
import { useAssistantGroupOrder } from '../hooks/useAssistantGroupOrder';
import { useConfirm } from '../providers/ConfirmProvider';
import PdfTemplateDialog from './PdfTemplateDialog';
import AssistantGroupDialog from './AssistantGroupDialog';
import AssistantAgentDialog from './AssistantAgentDialog';
import { useAuth } from '../firebase/AuthProvider';
import {
  assistantDraftKey,
  clearAssistantDraft,
  getAssistantDraft,
  initAssistantDrafts,
  isAssistantDraftsReady,
  setAssistantDraft,
} from '../utils/assistantDrafts';

// 入力のヒントになる例文 (セッションが空のときだけ表示)
const EXAMPLE_PROMPTS = [
  '明日の15時から1時間「定例ミーティング」を作成して',
  '今週の予定を教えて',
  '「請求書の確認」を明後日期限の ToDo に追加して',
  '「打合せメモ」という Keep メモを作って',
  '取引先へのお礼メールの下書きを作って',
];

const DEFAULT_EMPTY_TEXT =
  'AI にカレンダー・ToDo・Keep・Gmail 下書きの操作を依頼できます。例えば…';
// 空状態の案内を背景のコア (球体) 中心からどれだけ下げて置くか (ページ外枠の高さに対する割合)
const EMPTY_STATE_CORE_OFFSET = '24%';

interface Props {
  // カレンダー等を更新した可能性がある時に呼ぶ (一覧の再取得用)
  onMutated?: () => void;
  // アシスタント種別。会話一覧の分離に使う (既定 'gws')
  agentType?: AgentType;
  // 指定時はモード切替を隠し、そのモードに固定する (会計アシスタント用)
  fixedMode?: 'accounting';
  // 空状態に出す例文と説明文 (用途別に差し替え)
  examplePrompts?: string[];
  emptyStateText?: string;
  // 外枠の高さ。ダイアログ内に埋め込む場合などに上書きする (既定はページ全面)
  height?: string;
  // 空状態 (例文チップ) の配置。'core' は背景演出のコア (ParticleWaveBackground が書き出す
  // CSS 変数 --ai-core-x / --ai-core-y) に中心を合わせてページ中央へ重ねる。既定 'inline' は会話欄内に表示
  emptyStateAnchor?: 'inline' | 'core';
}

// 貼り付け画像を縮小して JPEG (data URL) に変換する。送信サイズとコストを抑えるため
// 長辺を maxDim に収め、透過は白背景で塗りつぶす。
async function blobToCompressedDataUrl(
  blob: Blob,
  maxDim = 1600,
  quality = 0.85
): Promise<string> {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context を取得できませんでした');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return canvas.toDataURL('image/jpeg', quality);
}

// --- ⊕ 添付ファイルの許可リストとサイズ上限 -------------------------------------
// 動画等の巨大ファイルによるタイムアウト・負荷を構造的に防ぐため、扱える種別だけを
// 明示的に受け付ける (許可リスト方式)。上限は Cloud Functions のリクエスト上限
// (32MB、base64 で約1.33倍に膨張) から安全マージンを見た値。
const MAX_ATTACH_FILE_BYTES = 10 * 1024 * 1024; // 1ファイル 10MB
const MAX_ATTACH_TOTAL_BYTES = 20 * 1024 * 1024; // 合計 20MB
const MAX_ATTACH_FILE_COUNT = 4;
const MAX_PASTE_IMAGES = 6;
// ファイル選択ダイアログの絞り込み (accept は突破可能なため下の正規表現で再検証する)
const ATTACH_ACCEPT =
  'image/*,.pdf,.xlsx,.docx,.pptx,.txt,.md,.csv,.tsv,.json,.log,.xml,.html,.htm,.yaml,.yml';
const ALLOWED_ATTACH_EXT_RE = /\.(pdf|xlsx|docx|pptx|txt|md|csv|tsv|json|log|xml|html?|ya?ml)$/i;
const LEGACY_OFFICE_EXT_RE = /\.(xls|doc|ppt)$/i;

// File を data URL ("data:<mime>;base64,...") として読み込む
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('ファイルの読み込みに失敗しました'));
    reader.readAsDataURL(file);
  });
}

export default function WorkspaceAssistantPanel({
  onMutated,
  agentType = 'gws',
  fixedMode,
  examplePrompts = EXAMPLE_PROMPTS,
  emptyStateText = DEFAULT_EMPTY_TEXT,
  height = 'calc(100vh - 160px)',
  emptyStateAnchor = 'inline',
}: Props) {
  const {
    sessions,
    loading: sessionsLoading,
    createSession,
    deleteSession,
    renameSession,
    setSessionGroup,
  } = useAssistantSessions(agentType);
  const {
    agents,
    groups,
    loading: agentsLoading,
    loaded: agentsLoaded,
    refetch: refetchAgents,
    saveGroup,
    deleteGroup,
    saveAgent,
    assignAgentGroup,
    deleteAgent,
  } = useAssistantAgents();
  const confirm = useConfirm();
  const notify = useNotification();
  const { user } = useAuth();

  // null = 新規 (未保存) セッション
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [pdfTemplateOpen, setPdfTemplateOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  // chat=質問 (全ツール) / report=レポート作成 (帳票PDF作成に絞る) / accounting=会計分析 (固定)
  const [mode, setMode] = useState<'chat' | 'report' | 'accounting'>(fixedMode ?? 'chat');

  // --- グループ (作業文脈の単位) ---------------------------------------------
  // 新規チャットが属するグループ ('' = 共通)。選択はセッション作成時に group_id として保存され、
  // バックエンドが注入する定義を「共通 + そのグループ」に絞る。既存セッションは保存済みの値を使う
  const [newChatGroupId, setNewChatGroupId] = useState('');
  // グループ一覧の初回取得が終わったか。初回だけプルダウンの代わりにローディングを出す
  // (AI 応答のたびに定義を再取得するため、毎回ローディングにすると選択欄がちらつく)
  const groupsLoadedOnce = agentsLoaded;
  // 「グループを変更」ダイアログの対象セッションと選択値
  const [moveTarget, setMoveTarget] = useState<AssistantSession | null>(null);
  const [moveValue, setMoveValue] = useState('');

  // グループの並び順は利用者ごとに保持し、一覧の見出しとプルダウンの両方に同じ順を使う
  // グループの並び順と開閉状態 (利用者ごとに Firestore へ記憶。'' は共通)
  const {
    sortGroups,
    saveGroupOrder,
    collapsedGroups,
    toggleGroupCollapsed: saveGroupCollapsed,
    prefsLoaded,
  } = useAssistantGroupOrder();
  const orderedGroups = sortGroups(groups);
  const enabledGroups = orderedGroups.filter((g) => g.enabled);
  const groupNameById = new Map(groups.map((g) => [g.id, g.name]));
  // 参照できないグループ id (削除済み・メンバー外) を持つセッションは共通として扱う
  const effectiveGroupOf = (s: AssistantSession) => (groupNameById.has(s.groupId) ? s.groupId : '');
  const currentSession = sessionId ? sessions.find((s) => s.id === sessionId) ?? null : null;
  const currentGroupId = currentSession ? effectiveGroupOf(currentSession) : newChatGroupId;
  const currentGroupName = currentGroupId ? groupNameById.get(currentGroupId) ?? '' : '';
  // グループ別の定義件数 (グループ管理ダイアログで削除可否の目安に出す)
  const agentCountByGroup = new Map<string, number>();
  for (const a of [...agents.rules, ...agents.skills, ...agents.workflows, ...agents.issues]) {
    if (a.group_id) agentCountByGroup.set(a.group_id, (agentCountByGroup.get(a.group_id) ?? 0) + 1);
  }

  const { messages, loading, restoring, error, send, stop, stopping } = useWorkspaceAssistant(
    sessionId,
    {
      onCreateSession: async (firstMessage) => {
        const id = await createSession(firstMessage, newChatGroupId);
        if (id) setSessionId(id);
        return id;
      },
      onTitleGenerated: (id, title) => {
        void renameSession(id, title);
      },
    },
    mode
  );

  // 入力欄は MessageComposer 内にローカル state として隔離している
  // (毎キーで会話エリアまで再描画されると重いため)。例文チップ等から値を流し込む
  // 必要があるので ref 経由で setText を呼ぶ。
  const composerRef = useRef<ComposerHandle | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);

  // 書きかけメッセージ (ドラフト) の保存先キー。セッション単位で分離し、
  // 未保存の新規チャットは選択中グループごとの 'new' 枠を使う (Firestore 永続化)。
  // グループを切り替えると、そのグループの書きかけに入力欄が切り替わる
  const draftKey = assistantDraftKey(agentType, sessionId, newChatGroupId);

  // --- 回答への評価 (👍👎) ---------------------------------------------------
  // 現在セッションの評価を購読し、メッセージ id で引く。👎 は理由種別 + 任意コメントを
  // ダイアログで受け取る。蓄積は規則の見直しと回答精度の検証 (事例 → 規則候補 → 検証ケース) に使う
  const { feedbackByMessage, setFeedback, clearFeedback } = useAssistantFeedback(sessionId);
  const [badTarget, setBadTarget] = useState<{ message: AssistantMessage; ascIndex: number } | null>(null);
  const [badCategory, setBadCategory] = useState<string>(FEEDBACK_CATEGORIES[0].id);
  const [badComment, setBadComment] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  // 評価の記録に添える「直前の依頼文」(昇順 messages で、その回答より前の最後のユーザー発話)
  const requestTextBefore = (ascIndex: number): string => {
    for (let j = ascIndex - 1; j >= 0; j -= 1) {
      if (messages[j].role === 'user') return messages[j].text;
    }
    return '';
  };

  const submitFeedback = async (
    message: AssistantMessage,
    ascIndex: number,
    rating: FeedbackRating,
    category?: string,
    comment?: string
  ): Promise<boolean> => {
    setFeedbackSaving(true);
    try {
      await setFeedback({
        message,
        rating,
        category,
        comment,
        requestText: requestTextBefore(ascIndex),
        mode,
        groupId: currentGroupId,
        agentType,
      });
      notify.success(rating === 'good' ? '「良い回答」として記録しました' : '問題のある回答として記録しました');
      return true;
    } catch (e) {
      console.error('回答評価の記録に失敗しました', e);
      notify.error('評価の記録に失敗しました');
      return false;
    } finally {
      setFeedbackSaving(false);
    }
  };

  // 👍 は即時記録、👎 は理由ダイアログへ。選択中のボタンを再度押すと取り消し
  const handleRate = (message: AssistantMessage, ascIndex: number, rating: FeedbackRating) => {
    if (!message.id) return;
    const current = feedbackByMessage.get(message.id);
    if (current?.rating === rating) {
      setFeedbackSaving(true);
      clearFeedback(message.id)
        .then(() => notify.info('評価を取り消しました'))
        .catch((e) => {
          console.error('回答評価の取り消しに失敗しました', e);
          notify.error('評価の取り消しに失敗しました');
        })
        .finally(() => setFeedbackSaving(false));
      return;
    }
    if (rating === 'good') {
      void submitFeedback(message, ascIndex, 'good');
      return;
    }
    setBadCategory(current?.category ?? FEEDBACK_CATEGORIES[0].id);
    setBadComment(current?.comment ?? '');
    setBadTarget({ message, ascIndex });
  };
  const submitBadFeedback = async () => {
    if (!badTarget) return;
    const ok = await submitFeedback(badTarget.message, badTarget.ascIndex, 'bad', badCategory, badComment.trim());
    if (ok) setBadTarget(null);
  };

  // 降順表示なので新しいメッセージは最上部に来る。増えたら会話エリアを最上部へスクロール。
  // 件数・応答待ち・会話の切替でだけ動かす (Firestore の更新通知は 1 回の質問で数回届き、
  // そのたびにスムーズスクロールをやり直すと描画が詰まって背景のコア演出がカクつくため)
  const messageCount = messages.length;
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messageCount, loading, sessionId]);

  // AI の応答が返るたびにカレンダー側の再取得と Agent 定義の再取得を促す
  // (AI が saveAgent/deleteAgent でルール等を変更した可能性があるため)
  const lastModelCount = useRef(0);
  // セッション切替でカウンタを戻す (応答数の多い会話から少ない会話へ移ると、
  // 件数が前セッションを超えるまで再取得が発火しなくなるため)
  useEffect(() => {
    lastModelCount.current = 0;
  }, [sessionId]);
  useEffect(() => {
    const modelCount = messages.filter((m) => m.role === 'model').length;
    if (modelCount > lastModelCount.current) {
      lastModelCount.current = modelCount;
      onMutated?.();
      void refetchAgents();
    }
  }, [messages, onMutated, refetchAgents]);

  // 設定ダイアログを開くたびに定義を再取得する (別ページ・別タブで AI が変更した場合も
  // 開いた時点の最新状態を表示できるようにする)
  useEffect(() => {
    if (agentDialogOpen) void refetchAgents();
  }, [agentDialogOpen, refetchAgents]);

  // 新規チャットへ切替。入力欄は draftKey の変化に伴い 'new' 枠のドラフトが
  // 復元されるため、ここでは明示クリアしない (書きかけの保護)
  const handleNewChat = () => {
    setSessionId(null);
  };

  // セッション操作の3点メニュー (幅が狭いため編集・削除をここに集約)
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement;
    session: AssistantSession;
  } | null>(null);
  // 名前変更ダイアログの対象と入力値
  const [renameTarget, setRenameTarget] = useState<AssistantSession | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // クリックで拡大表示する画像 URL (会話履歴の添付画像用。null で非表示)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const openSessionMenu = (e: React.MouseEvent<HTMLElement>, session: AssistantSession) => {
    e.stopPropagation();
    setMenuState({ anchorEl: e.currentTarget, session });
  };
  const closeSessionMenu = () => setMenuState(null);

  const startRename = () => {
    if (!menuState) return;
    setRenameTarget(menuState.session);
    setRenameValue(menuState.session.title);
    closeSessionMenu();
  };

  const submitRename = async () => {
    const title = renameValue.trim();
    if (renameTarget && title && title !== renameTarget.title) {
      await renameSession(renameTarget.id, title);
    }
    setRenameTarget(null);
  };

  const handleDeleteFromMenu = async () => {
    if (!menuState) return;
    const target = menuState.session;
    closeSessionMenu();
    const ok = await confirm({
      title: '会話の削除',
      message: 'この会話を削除しますか？元に戻せません。',
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;
    await deleteSession(target.id);
    // セッションに紐づく書きかけドラフトも一緒に破棄する
    clearAssistantDraft(assistantDraftKey(agentType, target.id));
    if (sessionId === target.id) setSessionId(null);
  };

  // 推論中フラグを Context へ放送する (AI エージェントページでは背景のコア演出が
  // 購読して反応する。購読者がいない画面では既定の no-op に流れるだけで何も起きない)
  const thinkingHub = useAssistantThinking();
  useEffect(() => {
    thinkingHub.announce(loading);
    return () => thinkingHub.announce(false); // unmount 時に思考中のまま残さない
  }, [loading, thinkingHub]);

  // 降順表示用に新しいメッセージが先頭へ来るよう反転
  const orderedMessages = [...messages].reverse();
  // Workflow チップは現在の文脈 (共通 + 現在グループ) のものだけ出す (注入される定義と揃える)
  const workflows = agents.workflows.filter((w) => !w.group_id || w.group_id === currentGroupId);

  // セッション一覧をグループ別のセクションに分ける (利用者の並び順のグループ、末尾に共通)。
  // 会話がまだ無いグループも出す (作成直後のグループが一覧に現れるように)。参照できないグループのセッションは共通へ寄せる
  const sessionSections: { id: string; name: string; sessions: AssistantSession[] }[] = [];
  {
    const byGroup = new Map<string, AssistantSession[]>();
    for (const s of sessions) {
      const gid = effectiveGroupOf(s);
      byGroup.set(gid, [...(byGroup.get(gid) ?? []), s]);
    }
    for (const g of orderedGroups) {
      sessionSections.push({ id: g.id, name: g.name, sessions: byGroup.get(g.id) ?? [] });
    }
    // 共通は末尾に固定する (ドラッグ対象外)
    if (byGroup.has('')) sessionSections.push({ id: '', name: '共通', sessions: byGroup.get('')! });
  }
  // グループ見出しのドラッグ並び替え。ドロップ先の位置を全グループの並び (orderedGroups) 上で
  // 計算して保存する (参照できないグループ等、表示上の並びと全グループの並びが一致するとは限らないため)
  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const sortableSectionIds = sessionSections.filter((s) => s.id).map((s) => s.id);
  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedGroups.map((g) => g.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    saveGroupOrder(arrayMove(ids, from, to)).catch((e) => {
      console.error('グループ並び順の保存に失敗しました', e);
      notify.error('並び順の保存に失敗しました');
    });
  };
  const toggleGroupCollapsed = (gid: string) => {
    saveGroupCollapsed(gid, ['', ...groups.map((g) => g.id)]).catch((e) => {
      console.error('グループの開閉状態の保存に失敗しました', e);
    });
  };

  // セッションの所属グループを変更する (3点メニュー → ダイアログ)
  const startMoveGroup = () => {
    if (!menuState) return;
    setMoveTarget(menuState.session);
    setMoveValue(effectiveGroupOf(menuState.session));
    closeSessionMenu();
  };
  const submitMoveGroup = async () => {
    if (moveTarget && moveValue !== effectiveGroupOf(moveTarget)) {
      await setSessionGroup(moveTarget.id, moveValue);
    }
    setMoveTarget(null);
  };

  return (
    <Box sx={{ mt: 2, px: 2, display: 'flex', gap: 2, height }}>
      {/* 左サイドバー: セッション一覧 */}
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          // 背景演出をそのまま見せるため完全透過 (枠線も付けない)
          bgcolor: 'transparent',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            onClick={handleNewChat}
            startIcon={<Icons.Add />}
            sx={STANDARD_CTA_BUTTON_SX}
          >
            新しいチャット
          </Button>
          {/* 新規チャットのグループ選択 + グループ管理。選んだグループの定義だけが共通と一緒に AI へ渡る。
              outlined の縮小ラベル「グループ」は枠の上端より上に出るため、ボタンと重ならない余白を取る */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
            {!groupsLoadedOnce ? (
              // 初回アクセス時はグループ一覧が届くまで選択欄を出さず、同じ高さでローディングを表示する
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  height: 40,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                {/* LoadingView の message は余白が大きいため、短い行では文言を横に添える */}
                <LoadingView message="" height="40px" size={18} />
                <Typography variant="caption" color="text.secondary">
                  グループを読み込み中...
                </Typography>
              </Box>
            ) : (
            <FormControl size="small" fullWidth>
              <InputLabel id="assistant-new-group-label" sx={{ fontSize: '0.8rem' }}>
                グループ
              </InputLabel>
              <Select
                labelId="assistant-new-group-label"
                label="グループ"
                value={sessionId ? currentGroupId : newChatGroupId}
                onChange={(e) => {
                  // 既存セッション表示中に変えた場合は、新規チャットへ切り替えてそのグループで始める
                  setNewChatGroupId(String(e.target.value));
                  if (sessionId) setSessionId(null);
                }}
                sx={{ fontSize: '0.8rem', bgcolor: '#fff' }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                  共通 (グループなし)
                </MenuItem>
                {enabledGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id} sx={{ fontSize: '0.8rem' }}>
                    {g.scope === 'shared' ? (
                      <Icons.Groups sx={{ fontSize: 14, mr: 0.75, color: blueGrey[400] }} />
                    ) : (
                      <Icons.Folder sx={{ fontSize: 14, mr: 0.75, color: blueGrey[400] }} />
                    )}
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            )}
            <Tooltip title="グループ管理">
              <IconButton size="small" onClick={() => setGroupDialogOpen(true)}>
                <Icons.Settings sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {/* 会話・グループ・表示設定 (並び順と開閉) がそろうまで一覧を出さない。
              会話だけ先に届くと、全件が「共通」に並んだ後でグループへ移る動きになるため */}
          {sessionsLoading || !groupsLoadedOnce || !prefsLoaded ? (
            <LoadingView message="読み込み中..." height="120px" size={28} />
          ) : sessionSections.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 3, px: 2 }}
            >
              会話履歴はまだありません
            </Typography>
          ) : (
            <List dense disablePadding>
              <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
              <SortableContext items={sortableSectionIds} strategy={verticalListSortingStrategy}>
              {sessionSections.map((sec) => (
                <SortableGroupSection key={sec.id || '__common__'} id={sec.id} enabled={groups.length > 0 && !!sec.id}>
                  {(handle) => (
                  <>
                  {/* グループ見出し (グループが 1 つも無ければ従来どおり見出し無しの平坦な一覧) */}
                  {groups.length > 0 && (
                    <ListItemButton
                      dense
                      onClick={() => toggleGroupCollapsed(sec.id)}
                      sx={{ py: 0.25, px: 1, minHeight: 28 }}
                    >
                      {collapsedGroups.has(sec.id) ? (
                        <Icons.ChevronRight sx={{ fontSize: 16, color: blueGrey[400] }} />
                      ) : (
                        <Icons.ExpandMore sx={{ fontSize: 16, color: blueGrey[400] }} />
                      )}
                      {sec.id ? (
                        <Icons.Folder sx={{ fontSize: 14, mx: 0.5, color: blueGrey[400] }} />
                      ) : (
                        <Icons.FolderOpen sx={{ fontSize: 14, mx: 0.5, color: blueGrey[400] }} />
                      )}
                      <ListItemText
                        primary={`${sec.name} (${sec.sessions.length})`}
                        primaryTypographyProps={{
                          noWrap: true,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: blueGrey[600],
                        }}
                      />
                      {/* 掴み手: ここを掴んでグループを並び替える (クリックの折り畳みとは分ける) */}
                      {handle && (
                        <Tooltip title="ドラッグして並び替え" placement="right" enterDelay={600}>
                          <Box
                            {...handle.attributes}
                            {...handle.listeners}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ display: 'flex', cursor: 'grab', color: blueGrey[300], ml: 0.5, touchAction: 'none' }}
                          >
                            <Icons.DragIndicator sx={{ fontSize: 16 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </ListItemButton>
                  )}
                  <Collapse in={!collapsedGroups.has(sec.id)} timeout="auto" unmountOnExit>
                    {sec.sessions.length === 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', pl: 4.5, py: 0.5, fontSize: '0.7rem' }}
                      >
                        会話はまだありません
                      </Typography>
                    )}
                    {sec.sessions.map((s) => (
                      <Tooltip
                        key={s.id}
                        title={s.title}
                        placement="right"
                        enterDelay={500}
                        // fixed 配置 (ビューポート基準) にする。既定の absolute 配置だと、
                        // スクロール追従中に画面外へはみ出した瞬間ページ文書が伸びて
                        // 本画面側の縦スクロールバーが一瞬出現するため
                        slotProps={{ popper: { popperOptions: { strategy: 'fixed' } } }}
                      >
                        <ListItemButton
                          selected={s.id === sessionId}
                          onClick={() => setSessionId(s.id)}
                          sx={{
                            '&:hover .session-menu': { visibility: 'visible' },
                            pl: groups.length > 0 ? 2.5 : undefined,
                          }}
                        >
                          {s.origin === 'gmail' ? (
                            // メール自動処理が作った会話 (自動の印)
                            <Icons.Bolt sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                          ) : (
                            <Icons.ChatBubbleOutline sx={{ fontSize: 16, mr: 1, color: blueGrey[300] }} />
                          )}
                          <ListItemText
                            primary={s.title}
                            primaryTypographyProps={{
                              noWrap: true,
                              fontSize: '0.8rem',
                            }}
                          />
                          <IconButton
                            size="small"
                            className="session-menu"
                            onClick={(e) => openSessionMenu(e, s)}
                            sx={{
                              // ホバー時とメニュー展開中のみ表示する
                              visibility: menuState?.session.id === s.id ? 'visible' : 'hidden',
                              ml: 0.5,
                            }}
                          >
                            <Icons.MoreVert sx={{ fontSize: 18 }} />
                          </IconButton>
                        </ListItemButton>
                      </Tooltip>
                    ))}
                  </Collapse>
                  </>
                  )}
                </SortableGroupSection>
              ))}
              </SortableContext>
              </DndContext>
            </List>
          )}
        </Box>
      </Box>

      {/* 右: 会話エリア */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* モード切替 (質問 / レポート作成)。会計モード固定時 (fixedMode) は切替を隠す */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {fixedMode === 'accounting' ? (
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icons.AutoAwesome sx={{ fontSize: 16 }} />
              OBC 仕訳の起票ゆれ検知・補助元帳の照会・要約に答えます
            </Typography>
          ) : (
            <>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_, v) => {
                  if (v) setMode(v);
                }}
                disabled={loading || restoring}
              >
                <ToggleButton value="chat" sx={{ textTransform: 'none', px: 1.5 }}>
                  <Icons.AutoAwesome sx={{ fontSize: 16, mr: 0.5 }} /> 質問
                </ToggleButton>
                <ToggleButton value="report" sx={{ textTransform: 'none', px: 1.5 }}>
                  <Icons.PictureAsPdf sx={{ fontSize: 16, mr: 0.5 }} /> レポート作成
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {mode === 'report'
                  ? '帳票(PDF)の作成に専念します（カレンダー等の操作は質問モードへ）'
                  : 'カレンダー・ToDo・Keep・Gmail などを操作できます'}
              </Typography>
            </>
          )}
          {/* 現在の会話の文脈 (グループ)。共通の時は出さない */}
          {currentGroupName && (
            <Tooltip title="この会話では、共通の定義とこのグループの定義が AI に渡されます">
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                icon={<Icons.Folder />}
                label={currentGroupName}
                sx={{ ml: 'auto', fontSize: '0.72rem', height: 22, bgcolor: '#fff' }}
              />
            </Tooltip>
          )}
        </Box>

        {/* 入力エリア (上部固定)。入力 state は MessageComposer 内に隔離している。
            推論中は行ごと完全透過にし (背景演出を見せる)、クリックも遮断したうえで、
            空いたスペースへ処理中の案内メッセージを重ねて表示する */}
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              opacity: loading ? 0 : 1,
              pointerEvents: loading ? 'none' : 'auto',
              transition: 'opacity 0.6s ease',
            }}
          >
            <MessageComposer
              ref={composerRef}
              draftKey={draftKey}
              disabled={loading || restoring}
              onSend={(text, imgs, files) => void send(text, imgs, files)}
              showPdfButton={fixedMode !== 'accounting'}
              onOpenPdf={() => setPdfTemplateOpen(true)}
              onOpenSettings={() => setAgentDialogOpen(true)}
            />
          </Box>

          {/* 推論中の案内 (入力欄の空きスペースに常時表示。脈動ドット + 全文の案内 + 停止ボタン)。
              誤って Enter で送った依頼をすぐ止められるよう、停止ボタンだけはクリックを通す */}
          <Fade in={loading} timeout={{ enter: 600, exit: 300 }}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end', // 右端寄せ
                pr: 1.5,
                gap: 1,
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  bgcolor: '#a855f7',
                  animation: 'assistant-thinking-pulse 1.2s ease-in-out infinite',
                  '@keyframes assistant-thinking-pulse': {
                    '0%, 100%': { opacity: 0.35, transform: 'scale(0.8)' },
                    '50%': { opacity: 1, transform: 'scale(1.2)' },
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: '#a855f7', fontWeight: 600 }}>
                {stopping
                  ? '停止しています… 実行中の操作があれば、終わり次第止まります'
                  : '回答を作成中です… 完了までページを離れても大丈夫です'}
              </Typography>
              <Tooltip title="AI の処理を止めます (停止までに実行済みの操作は取り消されません)">
                <span style={{ pointerEvents: loading ? 'auto' : 'none' }}>
                  <Button
                    onClick={() => void stop()}
                    disabled={stopping}
                    startIcon={<Icons.StopCircle />}
                    sx={{ ...STANDARD_DANGER_BUTTON_COMPACT_SX, flexShrink: 0 }}
                  >
                    停止
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Fade>
        </Box>

        {/* Workflow チップ (登録があれば表示) */}
        {workflows.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {workflows.map((w) => (
              <Tooltip key={w.id} title={w.description || w.name}>
                <Chip
                  size="small"
                  icon={w.scope === 'shared' ? <Icons.Groups /> : <Icons.Person />}
                  label={w.name}
                  onClick={() => void send(w.body)}
                  disabled={loading || restoring}
                  sx={{ fontSize: '0.72rem', cursor: 'pointer' }}
                />
              </Tooltip>
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}

        {/* 会話表示エリア (降順 = 新しいものが上) */}
        <Box
          sx={{
            flex: 1,
            mt: 1.5,
            overflowY: 'auto',
            borderRadius: 2,
            p: 2,
            // 背景演出をそのまま見せるため完全透過 (枠線も付けない)
            bgcolor: 'transparent',
          }}
        >
          {/* scrollIntoView が最上部を枠の上端に吸着させると p:2 の上余白が消えるため、
              scroll-margin-top で吸着位置をずらし、スクロール時も上マージンを残す */}
          <Box ref={topRef} sx={{ scrollMarginTop: '16px' }} />
          {restoring && <LoadingView message="会話を読み込み中..." height="80px" size={32} />}

          {messages.length === 0 && !loading && !restoring ? (
            <Box
              sx={
                emptyStateAnchor === 'core'
                  ? {
                      // ページ中央のコア (背景演出) に中心を合わせる。座標は ParticleWaveBackground が
                      // ページの外枠へ書き出す CSS 変数 (外枠基準の px)。絶対配置の基準はページ前面の
                      // Box (外枠と同じ原点) で、会話欄の枠を超えて配置される。初期化前は中央寄りの既定値
                      position: 'absolute',
                      left: 'var(--ai-core-x, 50%)',
                      // コア中心に重ねると球体と文字が重なりすぎるため、ページ高さの 3 割ぶん下げて
                      // 球体の下側に案内を置く (EMPTY_STATE_CORE_OFFSET で調整)
                      top: `calc(var(--ai-core-y, 55%) + ${EMPTY_STATE_CORE_OFFSET})`,
                      transform: 'translate(-50%, -50%)',
                      width: 'max-content',
                      maxWidth: 'min(720px, 80vw)',
                      textAlign: 'center',
                      color: 'text.secondary',
                      pointerEvents: 'none', // 文言部分はクリックを通し、チップだけ操作可能にする
                      '& .MuiChip-root': { pointerEvents: 'auto' },
                    }
                  : { textAlign: 'center', py: 6, color: 'text.secondary' }
              }
            >
              {/* コアに重ねる配置では球体自体が目印になるため、キラキラのアイコンは出さない */}
              {emptyStateAnchor !== 'core' && (
                <Icons.AutoAwesome sx={{ fontSize: 44, color: blueGrey[200] }} />
              )}
              <Typography variant="body2" sx={{ mt: emptyStateAnchor === 'core' ? 0 : 1, mb: 2 }}>
                {emptyStateText}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                {examplePrompts.map((ex) => (
                  <Chip
                    key={ex}
                    label={ex}
                    variant="outlined"
                    onClick={() => composerRef.current?.setText(ex)}
                    sx={{ fontSize: '0.75rem', cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            orderedMessages.map((m, i) => (
              <Box
                key={orderedMessages.length - 1 - i}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  mb: 2,
                  flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
                }}
              >
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: m.role === 'user' ? blueGrey[300] : '#607d8b',
                  }}
                >
                  {m.role === 'user' ? (
                    <Icons.Person sx={{ fontSize: 18 }} />
                  ) : (
                    <Icons.AutoAwesome sx={{ fontSize: 18 }} />
                  )}
                </Avatar>
                <Box
                  sx={{
                    maxWidth: '75%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    // 背景の青い波形と同化しないよう、ユーザー発言も AI 回答と同じ白にする
                    // (発言者は右寄せ + アバターで判別できる)
                    bgcolor: '#ffffff',
                    border: 1,
                    borderColor: 'divider',
                    wordBreak: 'break-word',
                  }}
                >
                  {m.role === 'user' ? (
                    // ユーザー発言はそのままプレーン表示 (改行保持)。画像・添付があればチップも表示。
                    // メール自動処理が作った発話は、元メールのカードを出し依頼文は折り畳む
                    <>
                      {m.mailSource && (
                        <Box sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: blueGrey[50], border: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Icons.Email sx={{ fontSize: 16, color: blueGrey[500] }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700] }}>
                              受信メール (自動処理)
                            </Typography>
                            <Chip label="自動" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {m.mailSource.subject}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {m.mailSource.from}
                            {m.mailSource.date ? ` ・ ${m.mailSource.date}` : ''}
                          </Typography>
                          {m.mailSource.attachments.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              添付: {m.mailSource.attachments.join('、')}
                            </Typography>
                          )}
                          {m.mailSource.gmailUrl && (
                            <Button
                              size="small"
                              component="a"
                              href={m.mailSource.gmailUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<Icons.OpenInNew sx={{ fontSize: 14 }} />}
                              sx={{ mt: 0.5, fontSize: '0.72rem', textTransform: 'none', p: 0, minWidth: 0 }}
                            >
                              Gmail で開く (返信の下書きはこのスレッドに入ります)
                            </Button>
                          )}
                        </Box>
                      )}
                      {m.files && m.files.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            mb: m.text || (m.images && m.images.length > 0) ? 1 : 0,
                          }}
                        >
                          {m.files.map((f, idx) =>
                            f.url ? (
                              <Chip
                                key={idx}
                                size="small"
                                icon={<Icons.AttachFile sx={{ fontSize: 16 }} />}
                                label={f.name}
                                component="a"
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                                sx={{ fontSize: '0.72rem', maxWidth: 260 }}
                              />
                            ) : (
                              <Chip
                                key={idx}
                                size="small"
                                icon={<Icons.AttachFile sx={{ fontSize: 16 }} />}
                                label={f.name}
                                sx={{ fontSize: '0.72rem', maxWidth: 260 }}
                              />
                            )
                          )}
                        </Box>
                      )}
                      {m.images && m.images.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: m.text ? 1 : 0 }}>
                          {m.images.map((src, idx) => (
                            <Box
                              component="img"
                              key={idx}
                              src={src}
                              alt={`添付画像 ${idx + 1}`}
                              onClick={() => setLightboxSrc(src)}
                              sx={{
                                maxWidth: 160,
                                maxHeight: 160,
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'divider',
                                cursor: 'zoom-in',
                                '&:hover': { opacity: 0.85 },
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      {m.text && m.mailSource ? (
                        // 自動処理の依頼文 (指示 + メール本文) は長いので折り畳む
                        <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontSize: '0.72rem', color: 'text.secondary' } }}>
                          <summary>AI への依頼文とメール本文を表示</summary>
                          <Typography variant="body2" sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap', mt: 0.5, fontSize: '0.78rem' }}>
                            {m.text}
                          </Typography>
                        </Box>
                      ) : m.text ? (
                        <Typography variant="body2" sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                          {m.text}
                        </Typography>
                      ) : null}
                    </>
                  ) : (
                    // AI 応答は Markdown としてレンダリング。保存済み (id あり) の回答には評価ボタンを付ける
                    <>
                      <MarkdownMessage>{m.text}</MarkdownMessage>
                      {m.id && (
                        <FeedbackButtons
                          current={feedbackByMessage.get(m.id)?.rating}
                          disabled={feedbackSaving}
                          onRate={(rating) => handleRate(m, orderedMessages.length - 1 - i, rating)}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* 👎 評価の理由入力ダイアログ */}
      <Dialog
        open={Boolean(badTarget)}
        onClose={() => !feedbackSaving && setBadTarget(null)}
        maxWidth="xs"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontSize: '0.875rem', py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.ThumbDownOutlined sx={{ fontSize: 18 }} /> 回答のどこが問題でしたか
        </DialogTitle>
        <DialogContent>
          {feedbackSaving ? (
            <LoadingView message="評価を記録中..." height={200} size={36} />
          ) : (
            <>
              <RadioGroup value={badCategory} onChange={(e) => setBadCategory(e.target.value)}>
                {FEEDBACK_CATEGORIES.map((c) => (
                  <FormControlLabel
                    key={c.id}
                    value={c.id}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{c.label}</Typography>}
                  />
                ))}
              </RadioGroup>
              <TextField
                value={badComment}
                onChange={(e) => setBadComment(e.target.value)}
                label="補足 (任意)"
                placeholder="例: 対象ファイルを確認せずに書き込んだ"
                multiline
                minRows={2}
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                記録した評価は、AI の規則の見直しと回答精度の検証に使います。
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBadTarget(null)} disabled={feedbackSaving} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button
            onClick={() => void submitBadFeedback()}
            disabled={feedbackSaving}
            startIcon={<Icons.ThumbDown />}
            sx={STANDARD_CTA_BUTTON_SX}
          >
            記録する
          </Button>
        </DialogActions>
      </Dialog>

      {/* セッション操作メニュー (3点リーダー) */}
      <Menu
        anchorEl={menuState?.anchorEl ?? null}
        open={Boolean(menuState)}
        onClose={closeSessionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={startRename}>
          <ListItemIcon>
            <Icons.Edit sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
            名前を変更
          </ListItemText>
        </MenuItem>
        {groups.length > 0 && (
          <MenuItem onClick={startMoveGroup}>
            <ListItemIcon>
              <Icons.SwapHoriz sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
              グループを変更
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Icons.Delete sx={{ fontSize: 18, color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>削除</ListItemText>
        </MenuItem>
      </Menu>

      {/* 会話名の変更ダイアログ */}
      <Dialog
        open={Boolean(renameTarget)}
        onClose={() => setRenameTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>会話の名前を変更</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submitRename();
              }
            }}
            inputProps={{ maxLength: 50 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button
            onClick={() => void submitRename()}
            startIcon={<Icons.Save />}
            sx={STANDARD_CTA_BUTTON_SX}
            disabled={!renameValue.trim()}
          >
            保存する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 会話の所属グループ変更ダイアログ */}
      <Dialog open={Boolean(moveTarget)} onClose={() => setMoveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 600, py: 0.75, px: 2 }}>
          会話のグループを変更
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            以降の発話から、選んだグループの定義が共通の定義と一緒に AI へ渡されます。
          </Typography>
          <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
            <InputLabel id="assistant-move-group-label">グループ</InputLabel>
            <Select
              labelId="assistant-move-group-label"
              label="グループ"
              value={moveValue}
              onChange={(e) => setMoveValue(String(e.target.value))}
              MenuProps={{ disableScrollLock: true }}
            >
              <MenuItem value="">共通 (グループなし)</MenuItem>
              {enabledGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveTarget(null)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={() => void submitMoveGroup()} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>
            保存する
          </Button>
        </DialogActions>
      </Dialog>

      {/* グループ管理ダイアログ (作成・編集・共有メンバー・削除) */}
      <AssistantGroupDialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        groups={groups}
        loading={agentsLoading}
        agentCountByGroup={agentCountByGroup}
        currentUserEmail={user?.email ?? ''}
        onSave={saveGroup}
        onDelete={deleteGroup}
      />

      {/* 帳票テンプレ設定ダイアログ (見積書) */}
      <PdfTemplateDialog open={pdfTemplateOpen} onClose={() => setPdfTemplateOpen(false)} />

      {/* Agent 定義ダイアログ (閲覧 + 画面編集 + グループ付け替え) */}
      <AssistantAgentDialog
        open={agentDialogOpen}
        onClose={() => setAgentDialogOpen(false)}
        loading={agentsLoading}
        agents={agents}
        groups={groups}
        currentGroupId={currentGroupId}
        currentUserEmail={user?.email ?? ''}
        onSaveAgent={saveAgent}
        onAssignGroup={assignAgentGroup}
        onDeleteAgent={deleteAgent}
        onOpenSession={(id) => setSessionId(id)}
      />

      {/* 会話履歴の画像クリック拡大表示 */}
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </Box>
  );
}

// --- 画像ライトボックス (クリック拡大表示。会話履歴・入力プレビューで共用) --------
// 画像・背景のどこをクリックしても閉じる (Esc も可)。src が null の間は非表示。
function ImageLightbox({ src, onClose }: { src: string | null; onClose: () => void }) {
  return (
    <Dialog
      open={!!src}
      onClose={onClose}
      onClick={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { bgcolor: 'transparent', boxShadow: 'none', m: 1, overflow: 'visible' },
      }}
    >
      {src && (
        <>
          <Box
            component="img"
            src={src}
            alt="拡大画像"
            sx={{
              maxWidth: '92vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 1,
              cursor: 'zoom-out',
              bgcolor: '#fff',
            }}
          />
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: -14,
              right: -14,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.55)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            }}
          >
            <Icons.Close sx={{ fontSize: 18 }} />
          </IconButton>
        </>
      )}
    </Dialog>
  );
}

// --- 入力欄 (会話エリアと再描画を分離するため独立コンポーネント化) ----------------

// 親から命令的に操作できる API (例文チップ等から入力欄へ値を流し込む)
export interface ComposerHandle {
  setText: (t: string) => void;
}

interface ComposerProps {
  // 書きかけメッセージの保存先キー ("<agentType>__<sessionId|new>")。
  // セッション切替で変わり、変化時に旧ドラフト保存→新ドラフト復元が走る
  draftKey: string;
  // 送信中・復元中は入力と送信を止める
  disabled: boolean;
  // 送信実行 (テキスト・貼り付け画像・⊕ 添付ファイルを親へ渡す)
  onSend: (text: string, images?: string[], files?: AttachedFileInput[]) => void;
  // 帳票テンプレートボタンを出すか (会計モードでは隠す)
  showPdfButton: boolean;
  onOpenPdf: () => void;
  onOpenSettings: () => void;
}

// 入力テキスト・貼り付け画像をこのコンポーネント内の state に閉じ込めることで、
// タイプ中に親 (会話エリアを含む) が再レンダリングされないようにする。

// 会話一覧のグループセクション (見出し + 配下のセッション) をドラッグで並び替えるための包み。
// 共通 (id='') は末尾固定で掴めない。掴み手 (⋮⋮) の listeners は children へ渡し、見出し内に置く
type GroupDragHandle = {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
};
function SortableGroupSection({
  id,
  enabled,
  children,
}: {
  id: string;
  enabled: boolean;
  children: (handle: GroupDragHandle | null) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: id || '__common__',
    disabled: !enabled,
  });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ opacity: isDragging ? 0.6 : 1, position: 'relative', zIndex: isDragging ? 1 : 'auto' }}
    >
      {children(enabled ? { attributes, listeners } : null)}
    </Box>
  );
}

// AI 回答の下に出す評価ボタン (👍 良い / 👎 悪い)。選択中は塗りつぶしアイコンで示し、
// 同じボタンをもう一度押すと取り消す。記録・取消の実体は親 (useAssistantFeedback) が持つ
function FeedbackButtons({
  current,
  disabled,
  onRate,
}: {
  current?: FeedbackRating;
  disabled: boolean;
  onRate: (rating: FeedbackRating) => void;
}) {
  const btn = (
    rating: FeedbackRating,
    title: string,
    Filled: React.ElementType,
    Outlined: React.ElementType
  ) => {
    const active = current === rating;
    const Icon = active ? Filled : Outlined;
    return (
      <Tooltip title={active ? `${title} (もう一度押すと取り消し)` : title}>
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => onRate(rating)}
            sx={{
              color: active ? (rating === 'good' ? 'primary.main' : 'error.main') : 'text.disabled',
              p: 0.5,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  };
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25, mt: 0.5 }}>
      {btn('good', '良い回答', Icons.ThumbUp, Icons.ThumbUpOutlined)}
      {btn('bad', '問題のある回答', Icons.ThumbDown, Icons.ThumbDownOutlined)}
    </Box>
  );
}

const MessageComposer = React.forwardRef<ComposerHandle, ComposerProps>(
  function MessageComposer(
    { draftKey, disabled, onSend, showPdfButton, onOpenPdf, onOpenSettings },
    ref
  ) {
    const [input, setInput] = useState('');
    const { user } = useAuth();
    const email: string | null = user?.email ?? null;

    // 入力値の更新は必ずここを通し、state とドラフト (Firestore デバウンス保存) を
    // 同時に更新する。例文チップからの setText 流し込みも同じ経路に乗せる
    const applyInput = (t: string) => {
      setInput(t);
      setAssistantDraft(draftKey, t);
    };

    // draftKey 変化時 (セッション切替・新しいチャット・初回マウント) にドラフトを復元。
    // 旧キーの書きかけは applyInput が都度キャッシュへ書いているため保存操作は不要
    useEffect(() => {
      let cancelled = false;
      if (isAssistantDraftsReady()) {
        // 読込済みならモジュールキャッシュから同期復元 (タイプ中の上書きなし)
        setInput(getAssistantDraft(draftKey));
      } else if (email) {
        void initAssistantDrafts(email).then(() => {
          // 初回読込の完了前にタイプが始まっていたら、そちらを優先して上書きしない
          if (!cancelled) setInput((cur) => cur || getAssistantDraft(draftKey));
        });
      }
      return () => {
        cancelled = true;
      };
    }, [draftKey, email]);
    // 貼り付け画像 (data URL)。送信時に一緒に渡し、会話内のみで保持する (履歴非保存)
    const [pendingImages, setPendingImages] = useState<string[]>([]);
    // ⊕ 添付ファイル (PDF/Office/テキスト)。size は上限検証用に保持する
    const [pendingFiles, setPendingFiles] = useState<(AttachedFileInput & { size: number })[]>([]);
    // クリックで拡大表示する貼り付け画像 (null で非表示)。親と分離した Composer 専用 state
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const notify = useNotification();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useImperativeHandle(ref, () => ({ setText: applyInput }), [draftKey]);

    const handleSend = () => {
      const text = input;
      const imgs = pendingImages;
      const files = pendingFiles;
      if (!text.trim() && imgs.length === 0 && files.length === 0) return;
      // 送信が確定した分のドラフトは破棄する
      applyInput('');
      setPendingImages([]);
      setPendingFiles([]);
      onSend(
        text,
        imgs.length > 0 ? imgs : undefined,
        files.length > 0 ? files.map(({ name, mimeType, dataUrl }) => ({ name, mimeType, dataUrl })) : undefined
      );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Enter で送信 (Shift+Enter は改行)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    // クリップボードの画像 (スクリーンショット等) を取り込む。縮小して data URL 化する
    const handlePaste = async (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const blobs: Blob[] = [];
      for (const it of Array.from(items)) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) blobs.push(f);
        }
      }
      if (blobs.length === 0) return;
      // 画像はテキスト欄に貼り込まず、プレビューへ回す
      e.preventDefault();
      try {
        const urls = await Promise.all(blobs.map((b) => blobToCompressedDataUrl(b)));
        setPendingImages((prev) => [...prev, ...urls].slice(0, MAX_PASTE_IMAGES));
      } catch (err) {
        console.error('画像の取り込みに失敗しました', err);
      }
    };

    const removePendingImage = (idx: number) => {
      setPendingImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const removePendingFile = (idx: number) => {
      setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    // ⊕ で選択したファイルを検証して取り込む。画像は貼り付けと同じ圧縮経路へ、
    // それ以外は許可リスト (種別) とサイズ上限で弾いてから data URL 化する
    const handleSelectFiles = async (selected: FileList | null) => {
      if (!selected || selected.length === 0) return;
      const files = Array.from(selected);
      let imageCount = pendingImages.length;
      let fileCount = pendingFiles.length;
      let totalBytes = pendingFiles.reduce((sum, f) => sum + f.size, 0);
      for (const f of files) {
        try {
          if (f.type.startsWith('image/')) {
            if (imageCount >= MAX_PASTE_IMAGES) {
              notify.warning(`画像は最大 ${MAX_PASTE_IMAGES} 枚までです`);
              continue;
            }
            const url = await blobToCompressedDataUrl(f);
            setPendingImages((prev) => [...prev, url].slice(0, MAX_PASTE_IMAGES));
            imageCount += 1;
            continue;
          }
          if (LEGACY_OFFICE_EXT_RE.test(f.name)) {
            notify.warning(
              `「${f.name}」は旧形式のため添付できません。新形式 (xlsx/docx/pptx) で保存し直してください`
            );
            continue;
          }
          if (!ALLOWED_ATTACH_EXT_RE.test(f.name)) {
            notify.warning(
              `「${f.name}」は未対応の形式です (対応: 画像 / PDF / Excel / Word / PowerPoint / テキスト系)`
            );
            continue;
          }
          if (f.size > MAX_ATTACH_FILE_BYTES) {
            notify.warning(`「${f.name}」はサイズ上限 (1ファイル10MB) を超えています`);
            continue;
          }
          if (fileCount >= MAX_ATTACH_FILE_COUNT) {
            notify.warning(`添付ファイルは最大 ${MAX_ATTACH_FILE_COUNT} 件までです`);
            continue;
          }
          if (totalBytes + f.size > MAX_ATTACH_TOTAL_BYTES) {
            notify.warning('添付ファイルの合計サイズ上限 (20MB) を超えるため追加できません');
            continue;
          }
          const dataUrl = await fileToDataUrl(f);
          setPendingFiles((prev) => [
            ...prev,
            { name: f.name, mimeType: f.type, dataUrl, size: f.size },
          ]);
          fileCount += 1;
          totalBytes += f.size;
        } catch (err) {
          console.error('ファイルの取り込みに失敗しました', err);
          notify.error(`「${f.name}」の読み込みに失敗しました`);
        }
      }
    };

    return (
      <>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="AI への依頼を入力（Enter で送信 / Shift+Enter で改行 / 画像は Ctrl+V で貼り付け）"
            value={input}
            onChange={(e) => applyInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={disabled}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#ffffff' } }}
          />
          <Button
            onClick={handleSend}
            startIcon={<Icons.Send />}
            sx={{ ...STANDARD_CTA_BUTTON_SX, flexShrink: 0, whiteSpace: 'nowrap', minWidth: 96 }}
            disabled={
              disabled ||
              (!input.trim() && pendingImages.length === 0 && pendingFiles.length === 0)
            }
          >
            送信
          </Button>
          {/* ⊕ ファイル添付 (画像 / PDF / Office / テキスト系)。送信ボタンの右に置く */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACH_ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => {
              void handleSelectFiles(e.target.files);
              // 同じファイルを選び直しても onChange が発火するよう毎回リセットする
              e.target.value = '';
            }}
          />
          <Tooltip title="ファイルを添付 (画像・PDF・Excel・Word・PowerPoint・テキスト)">
            <span>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                sx={{ flexShrink: 0, color: blueGrey[400] }}
              >
                <Icons.AttachFile />
              </IconButton>
            </span>
          </Tooltip>
          {showPdfButton && (
            <Tooltip title="帳票テンプレートフォーマットを開く">
              <IconButton
                onClick={onOpenPdf}
                sx={{ flexShrink: 0, color: blueGrey[400] }}
              >
                <Icons.PictureAsPdf />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="エージェント設定 (ルール / スキル / ワークフロー) を見る">
            <IconButton
              onClick={onOpenSettings}
              sx={{ flexShrink: 0, color: blueGrey[400] }}
            >
              <Icons.Settings />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 貼り付け画像のプレビュー (送信前) */}
        {pendingImages.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {pendingImages.map((src, idx) => (
              <Box
                key={idx}
                sx={{
                  position: 'relative',
                  width: 64,
                  height: 64,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={`貼り付け画像 ${idx + 1}`}
                  onClick={() => setPreviewSrc(src)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    cursor: 'zoom-in',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => removePendingImage(idx)}
                  sx={{
                    position: 'absolute',
                    top: 1,
                    right: 1,
                    p: 0.25,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <Icons.Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* ⊕ 添付ファイルのプレビュー (送信前)。名前チップ + 削除ボタン */}
        {pendingFiles.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {pendingFiles.map((f, idx) => (
              <Chip
                key={idx}
                size="small"
                icon={<Icons.AttachFile sx={{ fontSize: 16 }} />}
                label={`${f.name} (${(f.size / 1024 / 1024).toFixed(1)}MB)`}
                onDelete={() => removePendingFile(idx)}
                sx={{ fontSize: '0.72rem', maxWidth: 280 }}
              />
            ))}
          </Box>
        )}

        {/* 貼り付け画像プレビューのクリック拡大表示 */}
        <ImageLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />
      </>
    );
  }
);
